import { useReactiveVar } from '@apollo/client';
import React, { type ReactElement, type ReactNode, useEffect, useState } from 'react';
import styles from 'src/components/Tabs/PreviewTabs/PreviewTabsStyles';
import viewStateVar from 'src/store/viewState';

interface TabPaneProps {
  name: string;
  children?: ReactNode;
}

interface ChildContent {
  [key: string]: ReactNode;
}

type TabPaneChild = ReactElement<TabPaneProps> | false | null | undefined | '';

interface PreviewTabsProps {
  children: TabPaneChild | TabPaneChild[];
  defaultTab?: string | null;
}

const PreviewTabs: React.FC<PreviewTabsProps> = (props) => {
  const classes = styles();
  const viewState = useReactiveVar(viewStateVar);
  const { children, defaultTab } = props;
  const [tabHeader, setTabHeader] = useState<string[]>([]);
  const [childContent, setChildContent] = useState<ChildContent>({});
  const [active, setActive] = useState<string>('');

  useEffect(() => {
    const headers: string[] = [];
    const childCnt: ChildContent = {};

    React.Children.forEach(children, (element) => {
      if (!React.isValidElement(element)) return;
      const { name } = element.props as TabPaneProps;
      headers.push(name);
      childCnt[name] = (element.props as TabPaneProps).children;
    });

    setTabHeader(headers);
    setChildContent({ ...childCnt });
  }, [children]);

  const changeTab = (name: string): void => {
    setActive(name);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs only on mount to set initial active tab
  useEffect(() => {
    const childArray = React.Children.toArray(children);
    const firstChild = childArray[0] as ReactElement<TabPaneProps> | undefined;
    setActive(defaultTab || firstChild?.props?.name || 'Info');
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally runs only on mount to check initial horizontal state
  useEffect(() => {
    if (viewState.showHorizontal) {
      setActive('Info');
    }
  }, []);

  return (
    <div className={classes.tabs}>
      <ul className={classes.tabHeader}>
        {tabHeader.map((item) => (
          <li
            id={`cyp-tab-${item?.toLowerCase().split(' ').join('-')}`}
            key={item}
            className={item === active ? 'active' : ''}
          >
            <button
              type="button"
              onClick={() => changeTab(item)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                font: 'inherit',
                color: 'inherit',
                width: '100%',
              }}
            >
              {item}
            </button>
          </li>
        ))}
      </ul>
      <div className={classes.tabContent}>
        {Object.keys(childContent).map((key) => {
          if (key === active) {
            return (
              <div key={key} className={classes.tabChild}>
                {childContent[key]}
              </div>
            );
          } else {
            return null;
          }
        })}
      </div>
    </div>
  );
};

export default PreviewTabs;
